import React, { memo, useCallback, useEffect, useMemo } from "react";

import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  Text,
  Button,
  HStack,
  Box,
  Center,
  Spinner,
} from "@chakra-ui/react";
import colors from "app/colors";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import {
  DeleteJobMutation,
  DeleteJobMutationVariables,
  JobFragment,
  JobListQuery,
  JobListQueryVariables,
  JobOutputType,
  JobStatusType,
  RetryJobMutation,
  RetryJobMutationVariables,
} from "gql/graphql";
import JOB_LIST_QUERY from "./networking/job-list";
import { Virtuoso } from "react-virtuoso";
import FileThumb from "modules/files/file-thumb";
import moment from "moment";
import { HOME_PATH, MESSAGE_CALENDAR_FORMATS } from "modules/utils/Constants";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import {
  InfoIcon,
  NoSearchResultsIcon,
  PrintConversationIcon,
  TrashOutlineIcon,
  DownloadIcon,
  RetryIcon,
  SolidRightArrowIcon,
} from "modules/icons";
import Link from "modules/layouts/_shared/link";
import { usePrint } from "modules/providers/print-provider";
import DELETE_JOB_MUTATION from "./networking/delete-job";
import RETRY_JOB_MUTATION from "./networking/retry-job";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import useJobCount from "modules/hooks/use-job-count";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import { MESSAGE_THREAD_ID_QUERY_STRING } from "modules/hooks/use-query-string";

const DownloadsExportsDrawer = () => {
  const { closeDrawer, openModal } = useOverlay();
  const { data } = useQuery<JobListQuery, JobListQueryVariables>(
    JOB_LIST_QUERY,
  );
  const { resetUnseenJobCount, unseenJobCount } = useJobCount();

  const jobList = useMemo(() => data?.jobList ?? [], [data]);

  const onClearClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.DELETE_ALL_JOBS,
    });
  }, [openModal]);

  const computeItemKey = useCallback(
    (_index: number, job: JobFragment) => job.id,
    [],
  );

  const renderJob = useCallback(
    (_: any, job: JobFragment) => <Job job={job} />,
    [],
  );

  useEffect(() => {
    if (unseenJobCount > 0) resetUnseenJobCount();
  }, [resetUnseenJobCount, unseenJobCount]);

  return (
    <DrawerContent>
      <DrawerHeader px={3} pb={4}>
        <VStack spacing={1}>
          <HStack px={4} w="full">
            <DrawerCloseButton
              size="lg"
              position="unset"
              onClick={closeDrawer}
            />
            <Text fontWeight="bold" flex={1} textAlign="center">
              My Downloads & Exports
            </Text>
            {jobList.length > 0 && (
              <Button
                variant="outline"
                iconSpacing={1.5}
                px={2}
                py={1}
                h="fit-content"
                borderColor={colors.gray40}
                color={colors.gray40}
                onClick={onClearClicked}
              >
                Clear
              </Button>
            )}
          </HStack>
          <Text color={colors.gray50} fontSize={14} fontWeight={500}>
            Files available for download for 30 days
          </Text>
        </VStack>
      </DrawerHeader>

      <DrawerBody>
        {jobList.length === 0 ? (
          <VStack spacing={4}>
            <NoSearchResultsIcon />
            <Text fontSize={26} fontWeight={700}>
              No Files Available
            </Text>
            <Text fontSize={18} fontWeight={600} textAlign="center">
              Files recently exported for print or
              <br /> download are available here.
            </Text>
          </VStack>
        ) : (
          <Virtuoso
            data={jobList}
            computeItemKey={computeItemKey}
            itemContent={renderJob}
            defaultItemHeight={105}
          />
        )}
      </DrawerBody>

      <DrawerFooter />
    </DrawerContent>
  );
};

interface IJobProps {
  job: JobFragment;
}

const Job = ({ job }: IJobProps) => {
  const { closeDrawer } = useOverlay();
  const { print } = usePrint();
  const { cache } = useApolloClient();

  const [deleteJobAPI] = useMutation<
    DeleteJobMutation,
    DeleteJobMutationVariables
  >(DELETE_JOB_MUTATION);

  const [retryJobAPI] = useMutation<
    RetryJobMutation,
    RetryJobMutationVariables
  >(RETRY_JOB_MUTATION);

  const displayDownloadButton = useMemo(
    () =>
      job.status === JobStatusType.Complete &&
      (job.outputType === JobOutputType.Zip ||
        job.outputType === JobOutputType.Pdf),
    [job.status, job.outputType],
  );

  const displayPrintButton = useMemo(
    () =>
      job.status === JobStatusType.Complete &&
      job.outputType === JobOutputType.Pdf,
    [job.status, job.outputType],
  );

  const displayRetryButton = useMemo(
    () => job.status === JobStatusType.Error,
    [job.status],
  );

  const getHref = useCallback(
    (messageThreadId: string) => ({
      pathname: HOME_PATH,
      query: {
        [MESSAGE_THREAD_ID_QUERY_STRING]: messageThreadId,
      },
    }),
    [],
  );

  const onMessageThreadClicked = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  const onDownloadClicked = useCallback(() => {
    if (job.url) {
      const link = document.createElement("a");
      link.href = job.url;
      link.click();
    }
  }, [job.url]);

  const onPrintClicked = useCallback(() => {
    if (job.inlineUrl) print(job.inlineUrl);
  }, [job.inlineUrl, print]);

  const onDeleteJobClicked = useCallback(() => {
    deleteJobAPI({
      variables: {
        id: job.id,
      },
    });
    cache.updateQuery<JobListQuery>(
      {
        query: JOB_LIST_QUERY,
      },
      (data) => {
        if (!data) {
          return data;
        }
        return {
          ...data,
          jobList: data.jobList.filter((_job) => _job.id !== job.id),
        };
      },
    );
  }, [cache, deleteJobAPI, job.id]);

  const onRetryClicked = useCallback(() => {
    retryJobAPI({
      variables: {
        id: job.id,
      },
    });
  }, [job.id, retryJobAPI]);

  return (
    <HStack borderBottomWidth={1} borderBottomColor={colors.gray0} py={2}>
      <Box>
        {[JobStatusType.Created, JobStatusType.Started].some(
          (jobStatus) => jobStatus === job.status,
        ) && (
          <Box
            borderWidth={1}
            borderRadius={4}
            borderStyle="solid"
            borderColor={colors.grayPrimary}
            minW={86}
            minH={86}
          >
            <Center minH={84}>
              <Spinner />
            </Center>
          </Box>
        )}
        {job.status === JobStatusType.Error && (
          <Box
            borderWidth={1}
            borderRadius={4}
            borderStyle="solid"
            borderColor={colors.red10}
            minW={86}
            minH={86}
            display="grid" //vertically centers the content
          >
            <Center>
              <InfoIcon fill={colors.red10} boxSize={9} />
            </Center>
          </Box>
        )}
        {job.status === JobStatusType.Complete && (
          <Box cursor="pointer" onClick={onDownloadClicked}>
            {job.outputType === JobOutputType.Pdf && (
              <FileThumb
                contentType="application/pdf"
                name={job.outputName || ""}
              />
            )}
            {job.outputType === JobOutputType.Zip && (
              <FileThumb
                contentType="application/zip"
                name={job.outputName || ""}
              />
            )}
          </Box>
        )}
      </Box>
      <VStack w="full" alignItems="flex-start" spacing={0}>
        <Text noOfLines={1}>{job.outputName}</Text>
        {job.messageThreadId && (
          <Link
            href={getHref(job.messageThreadId)}
            color={colors.gray40}
            noOfLines={1}
            fontSize={16}
            onClick={onMessageThreadClicked}
          >
            <HStack spacing={1}>
              <SolidRightArrowIcon fill={colors.gray40} />
              <Text>View Conversation</Text>
            </HStack>
          </Link>
        )}
        <Text color={colors.grayMain} fontSize={14}>
          {moment(job.createTime).calendar(MESSAGE_CALENDAR_FORMATS)}
        </Text>
        {job.status === JobStatusType.Error && (
          <HStack spacing={1}>
            <InfoIcon fill={colors.red} boxSize={4} />
            <Text color={colors.red} fontSize={14} fontWeight={700}>
              Error processing file
            </Text>
            <Button
              color={colors.blue}
              fontSize={14}
              fontWeight={700}
              variant="link"
              onClick={onRetryClicked}
            >
              {"> Retry"}
            </Button>
          </HStack>
        )}
      </VStack>
      <HStack>
        <SyncoTooltip label="Delete">
          <CircularIconButton
            aria-label="delete job"
            size="sm"
            variant="outline"
            backgroundColor={colors.gray40}
            icon={<TrashOutlineIcon boxSize={5} fill={colors.white} />}
            onClick={onDeleteJobClicked}
          />
        </SyncoTooltip>
        {displayDownloadButton && job.url && (
          <SyncoTooltip label="Download">
            <CircularIconButton
              aria-label="download job output"
              size="sm"
              variant="outline"
              backgroundColor={colors.gray40}
              icon={<DownloadIcon boxSize={5} fill={colors.white} />}
              onClick={onDownloadClicked}
            />
          </SyncoTooltip>
        )}
        {displayPrintButton && job.inlineUrl && (
          <SyncoTooltip label="Print">
            <CircularIconButton
              aria-label="print job output"
              size="sm"
              variant="outline"
              backgroundColor={colors.gray40}
              icon={<PrintConversationIcon boxSize={5} fill={colors.white} />}
              onClick={onPrintClicked}
            />
          </SyncoTooltip>
        )}
        {displayRetryButton && (
          <SyncoTooltip label="Retry">
            <CircularIconButton
              aria-label="download job output"
              size="sm"
              variant="outline"
              backgroundColor={colors.gray40}
              icon={<RetryIcon boxSize={5} fill={colors.white} />}
              onClick={onRetryClicked}
            />
          </SyncoTooltip>
        )}
      </HStack>
    </HStack>
  );
};

export default memo(DownloadsExportsDrawer);
